import { getConsumerUrl } from './consumerUrl';

/**
 * Maps a landing use-case to a representative LIVE sample bot so visitors can
 * chat with a real example (feedback-call items 5 & 6 / Story 19.51).
 *
 * Slugs come from the curated demo bots in `getSampleBots`
 * (packages/database/src/queries.ts DEMO_BOT_SLUGS). A `null` entry means there
 * is no good same-language sample for that use case yet, so we fall back to the
 * full samples gallery rather than linking a bot in the wrong language.
 */
export type UseCaseKey = 'events' | 'services' | 'personalBrands';

// Slug -> primary_language is per the demo-bot seed (20260223000000_seed_demo_bots.sql).
// Each entry must point to a bot whose language matches the locale key, else the
// visitor lands on a bot in the wrong language. `null` => fall back to the gallery.
const SAMPLE_BOT_SLUGS: Record<UseCaseKey, Record<string, string | null>> = {
  events: {
    en: 'emma-jake-wedding-2026',
    he: 'taekwondo-championship-2026', // Hebrew event bot (techup-tel-aviv-2026 is English)
    es: 'emma-jake-wedding-2026-es',
  },
  services: {
    en: 'zen-flow-yoga',
    he: 'avi-repair-services',
    es: 'zen-flow-yoga-es',
  },
  // david-chen-music (en) + Hebrew/Spanish clones seeded in
  // 20260628000000_seed_personal_brand_demo_bots_he_es.sql.
  personalBrands: {
    en: 'david-chen-music',
    he: 'david-chen-music-he',
    es: 'david-chen-music-es',
  },
};

/**
 * Returns an absolute consumer-app URL for a live sample bot in the given use
 * case + locale, or the samples gallery when no same-language sample exists.
 */
export function getSampleBotUrl(useCase: UseCaseKey, locale: string): string {
  const consumerUrl = getConsumerUrl();
  const slug = SAMPLE_BOT_SLUGS[useCase]?.[locale];
  if (slug) return `${consumerUrl}/${locale}/${slug}`;
  return `${consumerUrl}/${locale}/samples`;
}
